import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Home, Search, Palette, Heart, LayoutDashboard, ChevronRight, ChevronLeft, X } from 'lucide-react';

const CONSUMER_STEPS = [
  { icon: Search, title: 'Search Properties', desc: 'Browse thousands of listings across India. Filter by city, budget, property type, and more to find your perfect home.' },
  { icon: Home, title: 'Rentals', desc: 'Looking to rent? Explore individual rentals, corporate housing, and vacation homes all in one place.' },
  { icon: Palette, title: 'Interior Design', desc: 'Get instant design quotes with our Z-Engine. Choose from multiple styles and get room-by-room cost breakdowns.' },
  { icon: Heart, title: 'Save & Compare', desc: 'Wishlist properties you love, save design quotes, and compare options from your personal dashboard.' },
  { icon: LayoutDashboard, title: 'Your Dashboard', desc: 'Track your listings, design projects, property management requests, and preferences — all in one place.' },
];

const AGENT_STEPS = [
  { icon: Home, title: 'Manage Listings', desc: 'Create and manage your property listings. Upload images, set pricing, and track views and inquiries.' },
  { icon: Search, title: 'View Leads', desc: 'Access your lead pipeline. Track new inquiries, follow up with prospects, and manage your sales funnel.' },
  { icon: LayoutDashboard, title: 'Site Visits', desc: 'Schedule and manage property site visits. Keep track of appointments and follow-ups.' },
  { icon: ChevronRight, title: 'Deal Pipeline', desc: 'Track deals from initial contact to closing. Monitor your conversion rates and revenue.' },
];

const DEVELOPER_STEPS = [
  { icon: Home, title: 'Add Projects', desc: 'List your development projects with detailed floor plans, tower configurations, and unit inventory.' },
  { icon: LayoutDashboard, title: 'Manage Inventory', desc: 'Track unit availability, pricing, and sales status across all your projects in real-time.' },
  { icon: Search, title: 'Lead Dashboard', desc: 'View and manage leads across all your projects. Assign to team members and track conversion.' },
  { icon: ChevronRight, title: 'Analytics', desc: 'Access detailed analytics on project performance, lead sources, and team productivity.' },
];

const OnboardingTutorial = ({ portalType = 'consumer', open, onClose, onComplete }) => {
  const [step, setStep] = useState(0);
  
  const steps = portalType === 'agent' ? AGENT_STEPS : portalType === 'developer' ? DEVELOPER_STEPS : CONSUMER_STEPS;
  const current = steps[step];
  const isLast = step === steps.length - 1;
  const Icon = current.icon;

  const handleComplete = () => {
    if (onComplete) onComplete();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="onboarding-tutorial-dialog">
        <DialogTitle className="sr-only">Welcome Tutorial</DialogTitle>
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div className="h-full bg-[#15803D] transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>

        <div className="p-8">
          {/* Skip button */}
          <div className="flex justify-end -mt-2 -mr-2 mb-2">
            <button onClick={handleComplete} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" data-testid="tutorial-skip-btn">
              Skip Tutorial <X className="w-3 h-3" />
            </button>
          </div>

          {/* Step content */}
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#15803D]/10 flex items-center justify-center">
              <Icon className="w-8 h-8 text-[#15803D]" />
            </div>
            <div>
              <p className="text-xs text-[#15803D] font-medium mb-1">Step {step + 1} of {steps.length}</p>
              <h3 className="text-xl font-semibold" data-testid="tutorial-step-title">{current.title}</h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed" data-testid="tutorial-step-desc">{current.desc}</p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button variant="ghost" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="rounded-none" data-testid="tutorial-prev-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            
            {/* Dots */}
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-[#15803D]' : 'bg-gray-200'}`} />
              ))}
            </div>

            {isLast ? (
              <Button onClick={handleComplete} className="rounded-none bg-[#15803D] hover:bg-[#166534]" data-testid="tutorial-complete-btn">
                Get Started <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={() => setStep(s => s + 1)} className="rounded-none bg-[#15803D] hover:bg-[#166534]" data-testid="tutorial-next-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingTutorial;
